import { AlertCircle } from 'lucide-react';
import type { ModerationAlert } from '@/types/userAdmin';

interface ModerationAlertListProps {
  alerts: ModerationAlert[];
  limit?: number;
}

/**
 * ModerationAlertList component displays moderation alerts for a user
 * 
 * @param {ModerationAlertListProps} props - Component properties
 * @returns {JSX.Element} Rendered moderation alert list
 */
export function ModerationAlertList({ alerts, limit }: ModerationAlertListProps) {
  const displayedAlerts = limit ? alerts.slice(0, limit) : alerts;
  const hasMore = limit && alerts.length > limit;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'dismissed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <div className="space-y-2">
      {displayedAlerts.map((alert) => (
        <div
          key={alert.id}
          className={`flex items-start gap-3 p-3 rounded-lg border-2 ${getSeverityColor(alert.severity)}`}
        >
          <AlertCircle className="mt-0.5 flex-shrink-0" size={18} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-white border">
                {alert.severity}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(alert.status)}`}>
                {alert.status}
              </span>
            </div>
            <p className="text-sm font-medium mb-1">{alert.description}</p>
            <p className="text-xs opacity-75">
              Created {formatDate(alert.created_at)}
            </p>
          </div>
        </div>
      ))}

      {hasMore && (
        <p className="text-sm text-center text-neutral-600 mt-2">
          and {alerts.length - limit!} more...
        </p>
      )}
    </div>
  );
}
